import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Send, Inbox } from "lucide-react";

/*
   Painel da imobiliária > Distribuir leads
   Gestor cadastra leads (individualmente) e distribui pros corretores.
   Ao distribuir, o corretor recebe WhatsApp de "lead novo" + o lead cai no funil dele.
*/
export default function AgencyDistributeLeads() {
  const [unassigned, setUnassigned] = useState([]);
  const [realtors, setRealtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null); // lead being assigned
  const [form, setForm] = useState({
    name: "", phone: "", email: "", interest: "",
    neighborhood: "", city: "", budget: "", bedrooms: "",
    notes: "", realtor_id: "",
  });
  const [selectedRealtor, setSelectedRealtor] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/agency/leads/unassigned"),
      api.get("/agency/realtors"),
    ]).then(([u, r]) => {
      setUnassigned(u.data.leads);
      setRealtors(r.data.realtors.filter((x) => x.is_active !== false));
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      // Coerce numeric
      if (payload.budget) payload.budget = parseFloat(payload.budget);
      else delete payload.budget;
      if (payload.bedrooms) payload.bedrooms = parseInt(payload.bedrooms);
      else delete payload.bedrooms;
      if (!payload.realtor_id) delete payload.realtor_id;
      await api.post("/agency/leads", payload);
      toast.success(payload.realtor_id ? "Lead cadastrado e distribuído!" : "Lead cadastrado na fila.");
      setCreateOpen(false);
      setForm({ name: "", phone: "", email: "", interest: "", neighborhood: "", city: "", budget: "", bedrooms: "", notes: "", realtor_id: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar lead");
    }
  };

  const submitAssign = async () => {
    if (!assignOpen || !selectedRealtor) return;
    try {
      await api.post(`/agency/leads/${assignOpen.id}/assign`, { realtor_id: selectedRealtor });
      toast.success("Lead distribuído! O corretor foi notificado por WhatsApp.");
      setAssignOpen(null);
      setSelectedRealtor("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao distribuir lead");
    }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="agency-distribute-leads">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-[#3B9EFF] font-bold">Distribuição</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold mt-1 text-white">Leads pra distribuir</h1>
          <p className="text-white/60 text-sm mt-2">
            Cadastre um lead novo e escolha o corretor. Ele recebe um WhatsApp na hora
            e o lead entra direto no funil dele em &quot;novo&quot;.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-create-agency-lead"
          className="bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white rounded-full px-5">
          <UserPlus className="w-4 h-4 mr-2" /> Novo lead
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-white/60">Carregando...</div>
      ) : unassigned.length === 0 ? (
        <div className="p-12 text-center bg-[#0F1F3D] border border-dashed border-white/15 rounded-2xl">
          <Inbox className="w-10 h-10 mx-auto text-white/40 mb-3" />
          <div className="text-white/70 font-medium">Nenhum lead na fila.</div>
          <div className="text-white/40 text-sm mt-1">Quando você cadastrar leads sem distribuir, eles aparecem aqui.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {unassigned.map((l) => (
            <div key={l.id} data-testid={`unassigned-lead-${l.id}`}
              className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white text-lg">{l.name}</div>
                <div className="text-white/60 text-sm mt-0.5">{l.phone} {l.interest && `· ${l.interest}`}</div>
                <div className="text-xs text-white/50 mt-2 flex flex-wrap gap-3">
                  {l.neighborhood && <span>Bairro: {l.neighborhood}</span>}
                  {l.budget && <span>Até R$ {Number(l.budget).toLocaleString("pt-BR")}</span>}
                  {l.bedrooms && <span>{l.bedrooms} dorms</span>}
                </div>
                {l.notes && <div className="text-xs text-white/50 mt-2 italic">&quot;{l.notes}&quot;</div>}
              </div>
              <Button onClick={() => setAssignOpen(l)} data-testid={`btn-assign-${l.id}`}
                className="bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white rounded-full">
                <Send className="w-4 h-4 mr-2" /> Distribuir
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Modal: create lead */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-create-lead">
          <DialogHeader>
            <DialogTitle>Novo lead</DialogTitle>
            <DialogDescription>
              Cadastre um lead que chegou pra imobiliária. Você pode já distribuir aqui
              ou deixar na fila pra decidir depois.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome completo</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Ana Beatriz Ramos" data-testid="input-lead-name" />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="48999998888" data-testid="input-lead-phone" />
              </div>
              <div>
                <Label>E-mail (opcional)</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="input-lead-email" />
              </div>
              <div className="col-span-2">
                <Label>Interesse</Label>
                <Input value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  placeholder="Ex: Apto 2 dorms até 500k" data-testid="input-lead-interest" />
              </div>
              <div>
                <Label>Bairro</Label>
                <Input value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })} />
              </div>
              <div>
                <Label>Cidade</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label>Orçamento (R$)</Label>
                <Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  placeholder="500000" />
              </div>
              <div>
                <Label>Quartos</Label>
                <Input type="number" min="0" max="10" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Observações</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Contexto do lead, canal de origem..." />
              </div>
              <div className="col-span-2">
                <Label>Distribuir agora pra qual corretor? (opcional)</Label>
                <Select value={form.realtor_id} onValueChange={(v) => setForm({ ...form, realtor_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="select-lead-realtor"><SelectValue placeholder="Deixar na fila" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Deixar na fila (distribuir depois)</SelectItem>
                    {realtors.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="submit-create-lead" className="bg-[#3B9EFF] hover:bg-[#1F7CE0]">
                Cadastrar lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: assign lead */}
      <Dialog open={!!assignOpen} onOpenChange={(v) => { if (!v) { setAssignOpen(null); setSelectedRealtor(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-assign-lead">
          <DialogHeader>
            <DialogTitle>Distribuir {assignOpen?.name}</DialogTitle>
            <DialogDescription>
              O corretor selecionado recebe um WhatsApp imediatamente com o resumo desse lead
              e o registro entra direto no funil dele em &quot;novo&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Corretor</Label>
              <Select value={selectedRealtor} onValueChange={setSelectedRealtor}>
                <SelectTrigger data-testid="select-assign-realtor"><SelectValue placeholder="Escolher corretor..." /></SelectTrigger>
                <SelectContent>
                  {realtors.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} <span className="text-stone-500 text-xs">({r.lead_count || 0} leads)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAssignOpen(null)}>Cancelar</Button>
              <Button onClick={submitAssign} disabled={!selectedRealtor}
                data-testid="submit-assign" className="bg-[#3B9EFF] hover:bg-[#1F7CE0]">
                <Send className="w-4 h-4 mr-2" /> Distribuir e notificar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
